import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_TITLE_ORDER,
  normalizeTitleOrder,
  type TitleLang,
} from '@movienight/shared';
import { saveTitleOrder } from './api';

/**
 * Which language a film's title is read in, for every surface at once.
 *
 * A context rather than a prop, because the list is spent six levels down (a
 * card inside a board inside a page) by components that otherwise have no
 * reason to know a session exists. It is the same argument the socket provider
 * makes, and the same one that would be wrong for anything the components
 * *act* on — this is an adjective, not a capability.
 *
 * ## The default is a value, not a throw
 *
 * `useTitleOrder` outside a provider returns `DEFAULT_TITLE_ORDER` instead of
 * throwing. Every test harness on this project mounts components in isolation,
 * and a title order is exactly the kind of ambient fact where "Serbian first,
 * like everybody starts" is a better answer than a crash — the harnesses would
 * otherwise each have to wrap a provider they do not care about, which is a
 * thing that gets forgotten and looks identical to a product bug when it is.
 *
 * The *writing* half is a separate hook with no default, because a component
 * that reorders the list without one is a real mistake: silently dropping the
 * write would be a sign that reorders itself and then forgets.
 */

interface TitleOrderControls {
  order: TitleLang[];
  /** Optimistic: the sign moves now, the row catches up. */
  reorder: (next: readonly TitleLang[]) => void;
  /** Non-null only while a save is failing, for the sign's own quiet apology. */
  error: string | null;
}

const OrderContext = createContext<readonly TitleLang[]>(DEFAULT_TITLE_ORDER);
const ControlsContext = createContext<TitleOrderControls | null>(null);

export function TitleOrderProvider({
  initial,
  children,
}: {
  /** From the session. The server's row is the source of truth on mount. */
  initial: readonly TitleLang[];
  children: React.ReactNode;
}) {
  const [order, setOrder] = useState<TitleLang[]>(() => normalizeTitleOrder(initial));
  const [error, setError] = useState<string | null>(null);

  /**
   * Re-seed when the session says something different — a fresh login, or this
   * member reordering the sign in another tab and the session being refetched.
   * Keyed on the joined list rather than the array, which is a new object on
   * every render of App and would otherwise stamp on a local reorder.
   */
  const initialKey = normalizeTitleOrder(initial).join(',');
  useEffect(() => {
    setOrder(initialKey.split(',') as TitleLang[]);
  }, [initialKey]);

  /**
   * Only the newest write's answer is allowed to land. Dragging a slat twice
   * quickly means two PUTs in flight, and a slow first response arriving after
   * a fast second one would put the sign back where it was a moment ago —
   * which reads as the site fighting your hand.
   */
  const writeId = useRef(0);

  const reorder = useCallback((next: readonly TitleLang[]) => {
    const normalized = normalizeTitleOrder(next);
    setOrder(normalized);
    setError(null);

    const id = ++writeId.current;
    void saveTitleOrder(normalized)
      .then((saved) => {
        if (writeId.current !== id) return;
        setOrder(saved);
      })
      .catch(() => {
        if (writeId.current !== id) return;
        // The sign stays where it was put. It is a preference, not a booking:
        // losing the write costs the next page load, and snapping the slats
        // back under somebody's finger is the worse of the two failures.
        setError('Nije sačuvano. Probaj opet.');
      });
  }, []);

  const controls = useMemo<TitleOrderControls>(
    () => ({ order, reorder, error }),
    [order, reorder, error],
  );

  return (
    <OrderContext.Provider value={order}>
      <ControlsContext.Provider value={controls}>{children}</ControlsContext.Provider>
    </OrderContext.Provider>
  );
}

/** The order, for anything that draws a title. Safe outside a provider. */
export function useTitleOrder(): readonly TitleLang[] {
  return useContext(OrderContext);
}

/** The order plus the way to change it. Only the sign uses this. */
export function useTitleOrderControls(): TitleOrderControls {
  const controls = useContext(ControlsContext);
  if (!controls) {
    throw new Error('useTitleOrderControls outside TitleOrderProvider');
  }
  return controls;
}
